import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { wheelApiService, PromoWheelItem, PromoWheelConfig, WheelStats, WheelUser } from '../services/wheelApi';
import { promotionApiService, Promotion } from '../services/promotionApi';

const WheelManager: React.FC = () => {
  const [config, setConfig] = useState<PromoWheelConfig | null>(null);
  const [items, setItems] = useState<PromoWheelItem[]>([]);
  const [promotions, setPromotions] = useState<Promotion[]>([]);
  const [stats, setStats] = useState<WheelStats | null>(null);
  const [users, setUsers] = useState<WheelUser[]>([]);
  const [selectedUser, setSelectedUser] = useState<WheelUser | null>(null);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingItem, setEditingItem] = useState<PromoWheelItem | null>(null);
  const [activeTab, setActiveTab] = useState<'items' | 'config' | 'stats' | 'users'>('items');
  
  // États pour les délais de regagner
  const [winDelayMinutes, setWinDelayMinutes] = useState(0);
  const [winDelayHours, setWinDelayHours] = useState(0);
  const [winDelayDays, setWinDelayDays] = useState(1);
  
  const [formData, setFormData] = useState({
    promotion: undefined as number | undefined,
    probability_weight: 1,
    is_active: true
  });

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    console.log('🎨 Items state updated:', items.length, items);
  }, [items]);

  useEffect(() => {
    if (config && config.win_delay_days !== undefined) {
      // Initialiser les valeurs des délais de regagner seulement au chargement
      const totalMinutes = Math.floor(config.win_delay_days * 24 * 60);
      const days = Math.floor(config.win_delay_days);
      const hours = Math.floor((config.win_delay_days * 24) % 24);
      const minutes = totalMinutes % 60;
      
      setWinDelayDays(days);
      setWinDelayHours(hours);
      setWinDelayMinutes(minutes);
    }
  }, [config?.id]); // Seulement quand la config change vraiment

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      console.log('🔄 Début du chargement des données...');
      console.log('🔑 User from localStorage:', JSON.parse(localStorage.getItem('user') || '{}'));
      
      const [configData, itemsData, promosData, statsData, usersData] = await Promise.all([
        wheelApiService.getConfig(),
        wheelApiService.getItems(),
        promotionApiService.getPromotions(),
        wheelApiService.getStats().catch(() => null),
        wheelApiService.getWheelUsers().catch(() => [])
      ]);
      
      console.log('📦 Données reçues - Items:', itemsData.length, itemsData);
      console.log('📦 Config:', configData);
      console.log('📦 Promos:', promosData.length);
      
      setConfig(configData);
      setItems(itemsData);
      setPromotions(promosData);
      setStats(statsData);
      setUsers(usersData);
      
      console.log('✅ State mis à jour - Items count:', itemsData.length);
    } catch (err: any) {
      console.error('❌ Erreur chargement:', err);
      setError(err.message || 'Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveConfig = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!config) return;

    try {
      setError(null);
      await wheelApiService.updateConfig({
        replay_delay_days: config.replay_delay_days,
        win_delay_days: config.win_delay_days,
        is_active: config.is_active
      });
      alert('Configuration sauvegardée!');
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleToggleUserBlock = async (userId: number) => {
    try {
      setError(null);
      const result = await wheelApiService.toggleUserWheelBlock(userId);
      alert(result.message);
      // Recharger les utilisateurs
      const usersData = await wheelApiService.getWheelUsers();
      setUsers(usersData);
    } catch (err: any) {
      setError(err.message);
    }
  };

  const showUserHistory = (user: WheelUser) => {
    setSelectedUser(user);
    setShowHistoryModal(true);
  };

  const handleSubmitItem = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const errors = wheelApiService.validateItemData(formData);
    if (errors.length > 0) {
      setError(errors.join(', '));
      return;
    }

    try {
      // Ajouter les valeurs par défaut pour les champs supprimés
      const dataToSend = {
        ...formData,
        is_no_win: false,
        is_guaranteed_win: false,
        is_first_time_prize: false,
        order: 0
      };

      if (editingItem) {
        await wheelApiService.updateItem(editingItem.id, dataToSend);
        alert('Item modifié avec succès!');
      } else {
        await wheelApiService.createItem(dataToSend);
        alert('Item ajouté avec succès!');
      }
      
      resetForm();
      await loadData();
      setError(null);
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleDeleteItem = async (item: PromoWheelItem) => {
    if (!window.confirm(`Supprimer "${item.display_label}" de la roue?`)) return;

    try {
      await wheelApiService.deleteItem(item.id);
      await loadData();
      setError(null);
    } catch (err: any) {
      setError(err.message);
    }
  };

  const resetForm = () => {
    setFormData({
      promotion: undefined,
      probability_weight: 1,
      is_active: true
    });
    setEditingItem(null);
    setShowAddModal(false);
  };

  const startEdit = (item: PromoWheelItem) => {
    setFormData({
      promotion: item.promotion || undefined,
      probability_weight: item.probability_weight,
      is_active: item.is_active
    });
    setEditingItem(item);
    setShowAddModal(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900">🎯 Gestion de la Roue de Promo</h2>
        <p className="text-gray-600">Configurez les items, probabilités et règles de la roue</p>
      </div>

      {/* Messages d'erreur */}
      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-red-700">{error}</p>
          <button
            onClick={() => setError(null)}
            className="mt-2 text-red-600 hover:text-red-800 text-sm underline"
          >
            Fermer
          </button>
        </div>
      )}

      {/* Onglets */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="flex space-x-8">
          <button
            onClick={() => setActiveTab('items')}
            className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors ${
              activeTab === 'items'
                ? 'border-purple-500 text-purple-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Items de la roue ({items.length})
          </button>
          <button
            onClick={() => setActiveTab('config')}
            className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors ${
              activeTab === 'config'
                ? 'border-purple-500 text-purple-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Configuration
          </button>
          <button
            onClick={() => setActiveTab('stats')}
            className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors ${
              activeTab === 'stats'
                ? 'border-purple-500 text-purple-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Statistiques
          </button>
          <button
            onClick={() => setActiveTab('users')}
            className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors ${
              activeTab === 'users'
                ? 'border-purple-500 text-purple-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Utilisateurs ({users.length})
          </button>
        </nav>
      </div>

      {/* Contenu des onglets */}
      {activeTab === 'items' && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <p className="text-sm text-gray-600">
              <span className="font-bold text-purple-600">{items.length}</span> item(s) configuré(s)
            </p>
            <button
              onClick={() => setShowAddModal(true)}
              className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 flex items-center space-x-2"
            >
              <span>➕</span>
              <span>Ajouter un item</span>
            </button>
          </div>

          {/* Liste des items */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {items.map((item) => (
              <motion.div
                key={item.id}
                layout
                className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm hover:shadow-md transition-shadow"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-2">
                      <span className="text-lg font-bold text-gray-900">{item.display_label}</span>
                      {!item.is_active && (
                        <span className="px-2 py-0.5 bg-gray-200 text-gray-600 rounded text-xs">Inactif</span>
                      )}
                    </div>
                    
                    <div className="text-sm space-y-1 text-gray-700">
                      <p>Probabilité: <span className="font-semibold text-gray-900">{item.probability_weight}</span></p>
                    </div>
                  </div>
                  
                  <div className="flex space-x-2">
                    <button
                      onClick={() => startEdit(item)}
                      className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg"
                      title="Modifier"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                    </button>
                    <button
                      onClick={() => handleDeleteItem(item)}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                      title="Supprimer"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          {items.length === 0 && (
            <div className="text-center py-12 bg-gray-50 rounded-lg">
              <p className="text-gray-600 mb-4">Aucun item configuré</p>
              <button
                onClick={() => setShowAddModal(true)}
                className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700"
              >
                Ajouter le premier item
              </button>
            </div>
          )}
        </div>
      )}

      {activeTab === 'config' && config && (
        <form onSubmit={handleSaveConfig} className="max-w-2xl space-y-6">
          <div className="bg-gradient-to-r from-purple-50 to-blue-50 border border-purple-200 rounded-lg p-6">
            <h3 className="text-lg font-semibold text-purple-900 mb-4">🎰 Configuration de la Roue</h3>
            
            {/* Délai entre deux jeux */}
            <div className="space-y-4">
              <h4 className="font-medium text-gray-800">⏱️ Délai entre deux jeux</h4>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Minutes</label>
                  <input
                    type="number"
                    min="0"
                    max="59"
                    value={Math.floor((config.replay_delay_days * 24 * 60) % 60)}
                    onChange={(e) => {
                      const minutes = parseInt(e.target.value) || 0;
                      const currentHours = Math.floor((config.replay_delay_days * 24) % 24);
                      const currentDays = Math.floor(config.replay_delay_days);
                      const totalMinutes = currentDays * 24 * 60 + currentHours * 60 + minutes;
                      setConfig({ ...config, replay_delay_days: totalMinutes / (24 * 60) });
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Heures</label>
                  <input
                    type="number"
                    min="0"
                    max="23"
                    value={Math.floor((config.replay_delay_days * 24) % 24)}
                    onChange={(e) => {
                      const hours = parseInt(e.target.value) || 0;
                      const currentMinutes = Math.floor((config.replay_delay_days * 24 * 60) % 60);
                      const currentDays = Math.floor(config.replay_delay_days);
                      const totalMinutes = currentDays * 24 * 60 + hours * 60 + currentMinutes;
                      setConfig({ ...config, replay_delay_days: totalMinutes / (24 * 60) });
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Jours</label>
                  <input
                    type="number"
                    min="0"
                    value={Math.floor(config.replay_delay_days)}
                    onChange={(e) => {
                      const days = parseInt(e.target.value) || 0;
                      const currentHours = Math.floor((config.replay_delay_days * 24) % 24);
                      const currentMinutes = Math.floor((config.replay_delay_days * 24 * 60) % 60);
                      const totalMinutes = days * 24 * 60 + currentHours * 60 + currentMinutes;
                      setConfig({ ...config, replay_delay_days: totalMinutes / (24 * 60) });
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                  />
                </div>
              </div>
              <p className="text-xs text-gray-600">
                Temps d'attente: <span className="font-semibold text-purple-600">
                  {Math.floor(config.replay_delay_days)} jour(s), {Math.floor((config.replay_delay_days * 24) % 24)} heure(s), {Math.floor((config.replay_delay_days * 24 * 60) % 60)} minute(s)
                </span>
              </p>
            </div>

            {/* Délai avant de regagner */}
            <div className="space-y-4 mt-6">
              <h4 className="font-medium text-gray-800">🏆 Délai avant de regagner</h4>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Minutes</label>
                  <input
                    type="number"
                    min="0"
                    max="59"
                    value={winDelayMinutes}
                    onChange={(e) => {
                      const minutes = parseInt(e.target.value) || 0;
                      setWinDelayMinutes(minutes);
                      // Calculer et mettre à jour la config sans déclencher le useEffect
                      const totalMinutes = winDelayDays * 24 * 60 + winDelayHours * 60 + minutes;
                      const newConfig = { ...config!, win_delay_days: totalMinutes / (24 * 60) };
                      setConfig(newConfig);
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Heures</label>
                  <input
                    type="number"
                    min="0"
                    max="23"
                    value={winDelayHours}
                    onChange={(e) => {
                      const hours = parseInt(e.target.value) || 0;
                      setWinDelayHours(hours);
                      const totalMinutes = winDelayDays * 24 * 60 + hours * 60 + winDelayMinutes;
                      const newConfig = { ...config!, win_delay_days: totalMinutes / (24 * 60) };
                      setConfig(newConfig);
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Jours</label>
                  <input
                    type="number"
                    min="0"
                    value={winDelayDays}
                    onChange={(e) => {
                      const days = parseInt(e.target.value) || 0;
                      setWinDelayDays(days);
                      const totalMinutes = days * 24 * 60 + winDelayHours * 60 + winDelayMinutes;
                      const newConfig = { ...config!, win_delay_days: totalMinutes / (24 * 60) };
                      setConfig(newConfig);
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                  />
                </div>
              </div>
              <p className="text-xs text-gray-600">
                Temps d'attente: <span className="font-semibold text-purple-600">
                  {winDelayDays} jour(s), {winDelayHours} heure(s), {winDelayMinutes} minute(s)
                </span>
              </p>
              <p className="text-xs text-gray-500">
                Temps avant qu'un gagnant puisse regagner un autre prix
              </p>
            </div>
          </div>

          <div className="flex items-center bg-gray-50 p-4 rounded-lg">
            <input
              type="checkbox"
              id="is_active"
              checked={config.is_active}
              onChange={(e) => setConfig({ ...config, is_active: e.target.checked })}
              className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
            />
            <label htmlFor="is_active" className="ml-3 block text-sm font-medium text-gray-700">
              🎯 Roue active et disponible pour les utilisateurs
            </label>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h4 className="font-semibold text-blue-900 mb-2">ℹ️ Règles de la roue</h4>
            <ul className="text-sm text-blue-800 space-y-1">
              <li>• <strong>Délai entre deux jeux:</strong> Temps d'attente avant qu'un utilisateur puisse rejouer</li>
              <li>• <strong>Délai avant de regagner:</strong> Temps d'attente avant qu'un gagnant puisse gagner à nouveau</li>
              <li>• Le poids de probabilité détermine les chances de gain de chaque item</li>
              <li>• Les admins peuvent jouer sans restriction de délai</li>
            </ul>
          </div>

          <button
            type="submit"
            className="w-full bg-gradient-to-r from-purple-600 to-blue-600 text-white px-4 py-3 rounded-lg hover:from-purple-700 hover:to-blue-700 font-semibold transition-all"
          >
            💾 Sauvegarder la configuration
          </button>
        </form>
      )}

      {activeTab === 'stats' && stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <div className="bg-blue-50 rounded-lg p-6">
            <p className="text-blue-600 text-sm font-medium mb-1">Total tours</p>
            <p className="text-3xl font-bold text-blue-900">{stats.total_spins}</p>
          </div>
          <div className="bg-green-50 rounded-lg p-6">
            <p className="text-green-600 text-sm font-medium mb-1">Gains</p>
            <p className="text-3xl font-bold text-green-900">{stats.total_wins}</p>
          </div>
          <div className="bg-red-50 rounded-lg p-6">
            <p className="text-red-600 text-sm font-medium mb-1">Pertes</p>
            <p className="text-3xl font-bold text-red-900">{stats.total_losses}</p>
          </div>
          <div className="bg-purple-50 rounded-lg p-6">
            <p className="text-purple-600 text-sm font-medium mb-1">Taux de gain</p>
            <p className="text-3xl font-bold text-purple-900">{stats.win_rate}%</p>
          </div>
          <div className="bg-orange-50 rounded-lg p-6">
            <p className="text-orange-600 text-sm font-medium mb-1">Premiers tours</p>
            <p className="text-3xl font-bold text-orange-900">{stats.total_first_spins}</p>
          </div>
          <div className="bg-gray-50 rounded-lg p-6">
            <p className="text-gray-600 text-sm font-medium mb-1">Utilisateurs</p>
            <p className="text-3xl font-bold text-gray-900">{stats.total_users}</p>
          </div>

          {Object.keys(stats.popular_wins).length > 0 && (
            <div className="col-span-full bg-white rounded-lg p-6 border border-gray-200">
              <h3 className="font-bold text-gray-900 mb-4">🏆 Prix les plus gagnés</h3>
              <div className="space-y-2">
                {Object.entries(stats.popular_wins)
                  .sort((a, b) => b[1] - a[1])
                  .map(([label, count]) => (
                    <div key={label} className="flex items-center justify-between">
                      <span className="text-gray-700">{label}</span>
                      <span className="font-bold text-purple-600">{count} fois</span>
                    </div>
                  ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Modal d'ajout/modification */}
      <AnimatePresence>
        {showAddModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          >
            <motion.div
              initial={{ scale: 0.95 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.95 }}
              className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
            >
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl font-bold text-gray-900">
                    {editingItem ? "Modifier l'item" : "Ajouter un item"}
                  </h2>
                  <button
                    onClick={resetForm}
                    className="p-2 hover:bg-gray-100 rounded-lg"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                <form onSubmit={handleSubmitItem} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Promotion associée *
                    </label>
                    <select
                      value={formData.promotion || ''}
                      onChange={(e) => setFormData({ ...formData, promotion: parseInt(e.target.value) || undefined })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                      required
                    >
                      <option value="">Sélectionnez une promotion</option>
                      {promotions.map((promo) => {
                        // Vérifier si cette promotion est déjà utilisée
                        const isUsed = items.some(item => 
                          item.promotion === Number(promo.id) && 
                          (!editingItem || item.id !== editingItem.id)
                        );
                        
                        return (
                          <option 
                            key={promo.id} 
                            value={promo.id}
                            disabled={isUsed}
                            style={isUsed ? { color: '#9ca3af', fontStyle: 'italic' } : {}}
                          >
                            {promo.name} ({promo.discount_display}){isUsed ? ' - Déjà utilisée' : ''}
                          </option>
                        );
                      })}
                    </select>
                    <p className="text-xs text-gray-500 mt-1">
                      Une promotion ne peut être ajoutée qu'une seule fois
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Poids de probabilité *
                    </label>
                    <input
                      type="number"
                      min="1"
                      value={formData.probability_weight}
                      onChange={(e) => setFormData({ ...formData, probability_weight: parseInt(e.target.value) || 1 })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                      required
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Plus le poids est élevé, plus la chance de sortir est grande
                    </p>
                  </div>

                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="is_active_item"
                      checked={formData.is_active}
                      onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                      className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                    />
                    <label htmlFor="is_active_item" className="ml-2 block text-sm text-gray-700">
                      Item actif dans la roue
                    </label>
                  </div>

                  <div className="flex space-x-3 pt-4">
                    <button
                      type="button"
                      onClick={resetForm}
                      className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                    >
                      Annuler
                    </button>
                    <button
                      type="submit"
                      className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                    >
                      {editingItem ? 'Modifier' : 'Ajouter'}
                    </button>
                  </div>
                </form>
              </div>
            </motion.div>
          </motion.div>
        )}

        {/* Onglet Utilisateurs */}
        {activeTab === 'users' && (
        <div>
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">👥 Utilisateurs de la roue</h3>
            <p className="text-sm text-gray-600">
              <span className="font-bold text-purple-600">{users.length}</span> utilisateur(s) ont joué à la roue
            </p>
            <p className="text-xs text-gray-500 mt-1">
              💡 Cliquez sur un utilisateur pour voir son historique complet
            </p>
          </div>

          {users.length === 0 ? (
            <div className="text-center py-12 bg-gray-50 rounded-lg">
              <p className="text-gray-600">Aucun utilisateur n'a encore joué à la roue</p>
            </div>
          ) : (
            <div className="space-y-2">
              {users.map((user) => (
                <div key={user.id} className="bg-white border border-gray-200 rounded-lg">
                  <div 
                    className="p-4 cursor-pointer hover:bg-gray-50 transition-colors"
                    onClick={() => showUserHistory(user)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4">
                        <div className="text-2xl">
                          📊
                        </div>
                        <div>
                          <h4 className="font-semibold text-gray-900">{user.full_name}</h4>
                          <p className="text-sm text-gray-600">{user.email}</p>
                        </div>
                        {user.is_wheel_blocked && (
                          <span className="px-2 py-1 bg-red-100 text-red-700 text-xs font-medium rounded-full">
                            🚫 Bloqué
                          </span>
                        )}
                      </div>
                      <div className="flex items-center space-x-4">
                        <div className="text-right text-sm">
                          <span className="font-medium text-gray-900">{user.total_spins} tours</span>
                          <span className="text-gray-500 mx-2">•</span>
                          <span className="font-medium text-green-600">{user.total_wins} gains</span>
                        </div>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleToggleUserBlock(user.id);
                          }}
                          className={`px-3 py-1 rounded-lg font-medium text-xs transition-colors ${
                            user.is_wheel_blocked
                              ? 'bg-green-100 text-green-700 hover:bg-green-200'
                              : 'bg-red-100 text-red-700 hover:bg-red-200'
                          }`}
                        >
                          {user.is_wheel_blocked ? '🔓 Débloquer' : '🚫 Bloquer'}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      </AnimatePresence>

      {/* Modal d'historique utilisateur */}
      <AnimatePresence>
        {showHistoryModal && selectedUser && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setShowHistoryModal(false)}
          >
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              transition={{ type: "spring", duration: 0.5 }}
              className="bg-white rounded-3xl shadow-2xl max-w-4xl w-full max-h-[80vh] overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900">📊 Historique complet ({selectedUser.all_spins.length} tours)</h3>
                    <p className="text-gray-600 mt-1">
                      {selectedUser.full_name} ({selectedUser.email})
                    </p>
                    <div className="flex items-center space-x-4 mt-2 text-sm">
                      <span className="font-medium text-gray-900">
                        {selectedUser.total_spins} tours au total
                      </span>
                      <span className="text-gray-500">•</span>
                      <span className="font-medium text-green-600">
                        {selectedUser.total_wins} gains
                      </span>
                      {selectedUser.is_wheel_blocked && (
                        <>
                          <span className="text-gray-500">•</span>
                          <span className="px-2 py-1 bg-red-100 text-red-700 text-xs font-medium rounded-full">
                            🚫 Bloqué
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={() => setShowHistoryModal(false)}
                    className="text-gray-400 hover:text-gray-600 text-2xl"
                  >
                    ✕
                  </button>
                </div>
              </div>

              <div className="p-6 overflow-y-auto max-h-[60vh]">
                {selectedUser.all_spins.length === 0 ? (
                  <div className="text-center py-12">
                    <p className="text-gray-500">Aucun historique disponible</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {selectedUser.all_spins.map((spin, index) => (
                      <div key={spin.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                        <div className="flex items-center space-x-4">
                          <div className="flex items-center justify-center w-8 h-8 bg-white rounded-full text-sm font-medium text-gray-600">
                            #{selectedUser.all_spins.length - index}
                          </div>
                          <div className="text-3xl">
                            {spin.has_won ? '🎉' : '😔'}
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">
                              {spin.winning_item_label}
                            </p>
                            {spin.promotion_name && (
                              <p className="text-sm text-green-600 font-medium">
                                🎁 {spin.promotion_name}
                              </p>
                            )}
                            <p className="text-xs text-gray-500">
                              {spin.has_won ? 'Victoire!' : 'Pas de chance cette fois'}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-medium text-gray-900">
                            {new Date(spin.spin_date).toLocaleDateString('fr-FR', {
                              day: '2-digit',
                              month: '2-digit',
                              year: 'numeric'
                            })}
                          </p>
                          <p className="text-xs text-gray-500">
                            {new Date(spin.spin_date).toLocaleTimeString('fr-FR', {
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default WheelManager;
